export const AssetAttributes = {
  name: 'name',
  serialNumber: 'serialNumber',
  siteId: 'siteId',
  zoneId: 'zoneId',
  groupId: 'groupId',
  lastCommunicationDate: 'lastCommunicationDate'
} as const

export const FunctionsAttributes = {
  hasActiveAlerts: 'hasActiveAlerts', // Expects true/false
  hasActiveAlertsOfType: 'hasActiveAlertsOfType', // Expects alert type id
  hasAlreadyCommunicated: 'hasAlreadyCommunicated', // Expects true/false
  hasLostCommunication: 'hasLostCommunication' // Expects true / false
}

export const LogicOperators = { and: '$and', or: '$or' } as const
export type LogicOperator = (typeof LogicOperators)[keyof typeof LogicOperators]

export const OperandTypes = {
  objectAttribute: 'OBJ_ATTRIBUTE',
  functions: 'FUNCTIONS',
  customFields: 'CUSTOM_FIELDS',
  capabilities: 'CAPABILITIES'
} as const

export type OperandType = (typeof OperandTypes)[keyof typeof OperandTypes]

export const BooleanOperators = {
  equal: '=',
  notEqual: '!='
} as const

export const StringOperators = {
  iLike: 'ILIKE',
  equal: '=',
  notEqual: '!=',
  notContains: 'NOT_CONTAINS',
  startsWith: 'STARTS_WITH'
} as const

export const EmptyCheckOperators = {
  isEmpty: 'IS_EMPTY',
  isNotEmpty: 'IS_NOT_EMPTY'
} as const

export const CustomFieldStringOperators = {
  ...StringOperators,
  ...EmptyCheckOperators
} as const

export const NumericOperators = {
  equal: '=',
  notEqual: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<='
  // between: 'BETWEEN'
} as const

export const DomainOperators = {
  equal: '=',
  notEqual: '!='
} as const

export const MultiSelectDomainOperators = {
  in: 'IN',
  notIn: 'NOT_IN'
} as const

type BooleanOperator = (typeof BooleanOperators)[keyof typeof BooleanOperators]
type StringOperator = (typeof StringOperators)[keyof typeof StringOperators]
type EmptyCheckOperator = (typeof EmptyCheckOperators)[keyof typeof EmptyCheckOperators]
type NumericOperator = (typeof NumericOperators)[keyof typeof NumericOperators]
type DomainOperator = (typeof DomainOperators)[keyof typeof DomainOperators]
type MultiSelectDomainOperator = (typeof MultiSelectDomainOperators)[keyof typeof MultiSelectDomainOperators]
type CustomFieldStringOperator = (typeof CustomFieldStringOperators)[keyof typeof CustomFieldStringOperators]
export type Operator =
  | StringOperator
  | NumericOperator
  | DomainOperator
  | BooleanOperator
  | MultiSelectDomainOperator
  | EmptyCheckOperator
  | CustomFieldStringOperator

// Operators that don't require a value
export const OperatorsWithoutValue = [EmptyCheckOperators.isEmpty, EmptyCheckOperators.isNotEmpty] as const

/**
 * Checks if an operator requires a value
 * @param operator - The operator to check
 * @returns true if the operator requires a value, false otherwise
 */
export const operatorRequiresValue = (operator: Operator): boolean => {
  return !(OperatorsWithoutValue as readonly string[]).includes(operator)
}

export type ReportCommunicationStatus = 'hasNeverCommunicated' | 'hasCommunicated'

export interface ReportFilters {
  assetTypeIds: string[]
  groupIds: number[]
  zoneIds: number[]
  routeIds: number[]
  alertKeys: string[]
  communicationStatus?: ReportCommunicationStatus
}

export interface AssetFilters {
  assetTypeIds: string[]
  assetIds?: number[]
  status?: string
  search?: string
  page?: number
  pageSize?: number
  sort?: string
  conditions?: ExpressionNode | null
  id?: number
  name?: string
  groupBy?: AssetFilterGrouping
  shareLevel?: 'PRIVATE' | 'PUBLIC_READ' | 'PUBLIC'
}

export interface ExpressionNode {
  operator: LogicOperator
  conditions?: Condition[]
  subNodes?: ExpressionNode[]
}

export type FilterValue = string | number | boolean | Date | string[] | number[] | null

export interface Condition {
  operand: string
  operandType: OperandType
  operator: Operator
  value: FilterValue
}

// Types for Pinia Store and UI components

export interface FilterClause {
  _id: string
  type: 'clause'
  property: string
  operator: Operator
  value: FilterValue
}

export interface FilterGroup {
  _id: string
  type: 'group'
  operator: LogicOperator
  children: FilterItem[]
}

export type FilterItem = FilterClause | FilterGroup

export type FilterClausePayload = Omit<FilterClause, '_id' | 'type'>
export type FilterGroupPayload = Omit<FilterGroup, '_id' | 'type'>

export type BasicFilters = Record<string, FilterValue[]>

export type ShareLevel = 'PRIVATE' | 'PUBLIC' | 'PUBLIC_READ'

export type AssetFilterGrouping = 'MODULE' | 'COMPONENT'
